/**
 * Which control a single-choice field renders with, and whether that control
 * carries its own name.
 *
 * A checkbox and a switch print their label beside themselves — that is what
 * makes them readable as a sentence: "☑ Est un client". Printed again above the
 * control by the field wrapper, the same words appear twice in a row, and a
 * card of five switches becomes ten lines saying five things.
 *
 * So the wrapper's label steps aside for those two, and only those two: a
 * dropdown, a radio group and a segmented control all name their *options*,
 * never the question, and without a label above them nothing on screen says
 * what is being answered.
 *
 * Kept here rather than inside the control because both ends need the answer —
 * the control to draw its own name, the wrapper to know not to.
 */

/** What each field type renders with when the placement asks for nothing. */
export const DEFAULT_WIDGETS = { select: 'dropdown', radio: 'radios', boolean: 'checkbox' };

/**
 * Which controls a type can render with, in the order a picker offers them —
 * the same list wherever a field is placed: a layout on the fiche, a question
 * in a form. Mirrors App\Enums\FieldWidget; a type absent from the map has one
 * rendering and shows no picker.
 */
export const WIDGETS_BY_TYPE = {
    radio: ['default', 'segmented', 'cards', 'dropdown'],
    select: ['default', 'segmented', 'cards', 'radios'],
    boolean: ['default', 'switch', 'segmented']
};

/** The controls a picker offers for a field type. */
export function widgetsFor(type) {
    return WIDGETS_BY_TYPE[type] || [];
}

/** A control as a picker names it. */
export function widgetLabel(widget) {
    const labels = {
        default: i18n.t('Default'),
        radios: i18n.t('Radio buttons'),
        segmented: i18n.t('Segmented buttons'),
        cards: i18n.t('Cards'),
        dropdown: i18n.t('Dropdown list'),
        switch: i18n.t('Switch')
    };

    return labels[widget] || widget;
}

/**
 * @param {{type: string, widget?: string|null}} field
 * @returns {string} the widget the field renders with.
 */
export function choiceWidget(field) {
    const requested = field?.widget;

    if (requested && requested !== 'default') {
        return requested;
    }

    return DEFAULT_WIDGETS[field?.type] ?? 'radios';
}

/**
 * Whether the control prints the field's own name beside itself, which is what
 * makes a label above it a repetition.
 *
 * @param {{type: string, widget?: string|null}} field
 */
export function labelsItself(field) {
    const widget = choiceWidget(field);

    return widget === 'checkbox' || widget === 'switch';
}
